'use client';

import React from 'react';
import { SWRConfig, type SWRConfiguration } from 'swr';

import { isRetryableError } from './fetcher';
import { fetchAreaResource } from './resources';

/**
 * App-wide SWR defaults. Most are SWR's own, stated so they are visible and so
 * tests can start from the same place (`tests/component/swrTestUtils.tsx`).
 */
export const swrDefaults: SWRConfiguration = {
  fetcher: fetchAreaResource as SWRConfiguration['fetcher'],
  // Stale-while-revalidate: cached data at once, re-checked when a consumer
  // mounts, the window regains focus / the tab becomes visible, and the
  // browser comes back online.
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  // Mounts within this window share one request (a page of chips = one fetch).
  dedupingInterval: 2000,
  // Focus revalidates at most this often.
  focusThrottleInterval: 5000,
  // A 4xx is an answer — don't retry it on a timer. 5xx / network: a few
  // backed-off retries, then wait for the next focus / reconnect / mount.
  shouldRetryOnError: isRetryableError,
  errorRetryCount: 3,
  // A key change (another resort or sport) must never show the old key's data.
  keepPreviousData: false,
};

/**
 * Mounted once at the app root (`app/providers.tsx`).
 *
 * Deliberately no `provider`: the app uses SWR's default cache, which is what
 * the global `mutate` from 'swr' reaches — the `invalidate…` helpers that run
 * outside components (e.g. `invalidateFormFolders`) depend on that.
 */
export default function DataProvider({ children }: { children: React.ReactNode }) {
  return <SWRConfig value={swrDefaults}>{children}</SWRConfig>;
}
